import { getClubBanner } from '../../clubBanners'
import QuestEmptyShield from '../QuestEmptyShield'
import { SHIELD_SIZE, type MatchQuestPick, type OverUnderQuestPick, type QuestPick, type QuestResultData, type TeamQuestPick } from './questTypes'

// The caption under a voided quest's "No result" — one word, in the slot the
// winning figure would have occupied, for all three pick types.
const VOID_CAPTION = 'Voided'

// Result-state truth caption — "Correct" / a miss's actual figure / an
// unplayed card's "Correct answer" label, paired with the big truth number.
function ResultTruth({ big, small }: { big?: string; small?: string }) {
  if (!big && !small) return null
  return (
    <div className="quest-card__result-truth quest-card__result-truth--center">
      {big && <span className="quest-card__result-big">{big}</span>}
      {small && <span className="quest-card__result-small label-eyebrow">{small}</span>}
    </div>
  )
}

export function TeamResult({ pick, result }: { pick: TeamQuestPick; result: QuestResultData }) {
  const banner = pick.team ? getClubBanner(pick.team) : undefined
  const shield = banner ? (
    <img className="quest-card__shield" src={banner} alt="" aria-hidden="true" />
  ) : (
    <QuestEmptyShield size={SHIELD_SIZE} className="quest-card__shield" />
  )
  const derived =
    result.outcome === 'hit'
      ? 'Correct'
      : result.outcome === 'unplayed'
        ? 'Correct answer'
        : result.outcome === 'void'
          ? VOID_CAPTION
          : 'Winning figure'
  // `null` is the builder saying "no caption", not "none supplied": a quest with no winner puts
  // its verdict in the big slot on a miss, and there is no winning figure to label.
  const small = result.statCaption === undefined ? derived : result.statCaption ?? undefined
  return (
    <div className="quest-card__result quest-card__result--team">
      {shield}
      <ResultTruth big={result.statLine} small={small} />
    </div>
  )
}

export function MatchResult({ pick, result }: { pick: MatchQuestPick; result: QuestResultData }) {
  const game = pick.game
  const homeBanner = game ? getClubBanner(game.homeTeam) : undefined
  const awayBanner = game ? getClubBanner(game.awayTeam) : undefined
  const homeShield = homeBanner ? (
    <img className="quest-card__shield" src={homeBanner} alt="" aria-hidden="true" />
  ) : (
    <QuestEmptyShield size={SHIELD_SIZE} className="quest-card__shield" />
  )
  const awayShield = awayBanner ? (
    <img className="quest-card__shield" src={awayBanner} alt="" aria-hidden="true" />
  ) : (
    <QuestEmptyShield size={SHIELD_SIZE} className="quest-card__shield" />
  )
  const small =
    result.outcome === 'hit'
      ? 'Correct'
      : result.outcome === 'unplayed'
        ? 'Correct answer'
        : result.outcome === 'void'
          ? VOID_CAPTION
          : result.yourFigureLine
  return (
    <div className="quest-card__result quest-card__result--match">
      <div className="quest-card__match-shields">
        {homeShield}
        <span className="quest-card__match-divider" aria-hidden="true">
          &ndash;
        </span>
        {awayShield}
      </div>
      <ResultTruth big={result.truthLine} small={small} />
    </div>
  )
}

export function OverUnderResult({ pick, result }: { pick: OverUnderQuestPick; result: QuestResultData }) {
  const hasPick = pick.side !== undefined
  const missed = hasPick && result.outcome === 'miss'
  return (
    <div className="quest-card__ou quest-card__ou--result">
      <div className="quest-card__ou-read">
        <span className="quest-card__ou-line">{pick.line}</span>
      </div>
      {hasPick && (
        <div className="quest-card__ou-picked">
          <span
            className={`quest-card__ou-side quest-card__ou-side--active${
              missed ? ' quest-card__ou-side--struck' : ''
            }`}
          >
            {pick.side === 'under' ? 'UNDER' : 'OVER'}
          </span>
        </div>
      )}
      <ResultTruth
        big={result.trueValueLine}
        // No side hit on a voided quest — there is no total to have cleared the line.
        small={
          result.outcome === 'void'
            ? VOID_CAPTION
            : result.trueSide === 'under'
              ? 'Under'
              : 'Over'
        }
      />
    </div>
  )
}

export function QuestResultSlot({ pick, result }: { pick: QuestPick; result: QuestResultData }) {
  if (pick.type === 'team') return <TeamResult pick={pick} result={result} />
  if (pick.type === 'match') return <MatchResult pick={pick} result={result} />
  return <OverUnderResult pick={pick} result={result} />
}
